import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { logout } from "../redux/sessionSlice";
import "../css/dashboard.css";

export default function EventDashboard() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.session);
  const [events, setEvents] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_type: "donation",
    event_date: "",
    venue: "",
    posted_by: "",
    image: null,
  });
  const [editingEventId, setEditingEventId] = useState(null);  // Track editing event

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (user && user.id) {
      setFormData((prevState) => ({
        ...prevState,
        posted_by: user.id, // Correctly set the user ID
      }));
    }
  }, [user]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get("http://127.0.0.1:8000/api/events/");
      
      const filteredEvents = response.data.map((event) => {
        const filteredEvent = {};
  
        Object.keys(event).forEach((key) => {
          if (event[key] !== null && key !== "posted_by") {
            // Convert event_date from "2025-04-04T00:00:00+03:00" → "2025-04-04"
            if (key === "event_date") {
              filteredEvent[key] = event[key].split("T")[0]; 
            } else {
              filteredEvent[key] = event[key];
            }
          }
        });
  
        return filteredEvent;
      });
  
      setEvents(filteredEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };
  

  const handleLogout = () => {
    dispatch(logout());
    window.location.href = "/login";
  };

  const handleChange = (e) => {
    if (e.target.name === "image") {
      setFormData({ ...formData, image: e.target.files[0] });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get the logged-in user ID from local storage (or your auth state)
    const loggedInUser = JSON.parse(localStorage.getItem("user")); // Change this if you store user info differently
    const userId = loggedInUser ? loggedInUser.id : null;
  
    if (!userId) {
      console.error("User is not logged in. Cannot submit event.");
      return;
    }

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "posted_by") {
        formDataToSend.append(key, parseInt(userId, 10)); // Convert to integer
      } else {
        formDataToSend.append(key, formData[key]);
      }
    });

    try {
      if (editingEventId) {
        await axios.put(`http://127.0.0.1:8000/api/events/${editingEventId}/`, formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        alert("Event updated successfully");
      } else {
        await axios.post("http://127.0.0.1:8000/api/events/", formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        alert("Event created successfully");
      }
      fetchEvents();
    } catch (error) {
      console.error("Error submitting event", error);
    }
  };

  // Edit Event: Pre-fill the form with the event data
  const editEvent = (eventId) => {
    const eventToEdit = events.find((event) => event.uuid === eventId);
    if (eventToEdit) {
      setFormData({
        title: eventToEdit.title,
        description: eventToEdit.description,
        event_type: eventToEdit.event_type,
        event_date: eventToEdit.event_date,
        venue: eventToEdit.venue,
        posted_by: eventToEdit.posted_by,
        image: null, // Handle file upload separately
      });
      setEditingEventId(eventId);
    }
  };

  // Delete Event: Show confirmation alert before deleting
  const deleteEvent = (eventId) => {
    const confirmation = window.confirm("Are you sure you want to delete this event?");
    if (confirmation) {
      axios
        .delete(`http://127.0.0.1:8000/api/events/${eventId}/`)
        .then(() => {
          alert("Event deleted successfully");
          fetchEvents();
        })
        .catch((error) => {
          console.error("Error deleting event", error);
          alert("Failed to delete event.");
        });
    }
  };

  return (
    <div className="body-container">
      {/* Profile Section */}
      <div className="profile-container">
        <div className="profile-info">
          <span className="username">{user ? user.email : ""}</span>
          <span className="role">{user ? user.roles : ""}</span>
        </div>
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {/* Create Event Form */}
      <div className="card">
        <div className="card-header">{editingEventId ? "Edit Event" : "Create an Event"}</div>
        <form onSubmit={handleSubmit}>
          <input className="input" name="title" placeholder="Title" value={formData.title} onChange={handleChange} required />
          <textarea className="textarea" name="description" placeholder="Description" value={formData.description} onChange={handleChange} required />
          <select className="select" name="event_type" value={formData.event_type} onChange={handleChange}>
            <option value="wedding">Wedding</option>
            <option value="donation">Donation</option>
            <option value="development">Development</option>
            <option value="church_student_donation">Church Student Donation</option>
            <option value="singing_service">Singing Service</option>
          </select>
          <input className="input" type="date" name="event_date" value={formData.event_date} onChange={handleChange} required />
          <input className="input" name="venue" placeholder="Venue" value={formData.venue} onChange={handleChange} required />
          <input className="input" type="file" name="image" onChange={handleChange} />
          <button className="button" type="submit">Submit Event</button>
        </form>
      </div>

      {/* Events List */}
      <h2 className="card-header">Upcoming Events</h2>
      <div className="event-grid">
        {events.length > 0 ? events.map((event, index) => (
          <div key={index} className="event-card">
            {event.title && <h3>{event.title}</h3>}
            {event.description && <p>{event.description}</p>}
            {event.event_type && <p><strong>Type:</strong> {event.event_type}</p>}
            {event.event_date && <p><strong>Date:</strong> {new Date(event.event_date).toLocaleDateString()}</p>}
            {event.venue && <p><strong>Venue:</strong> {event.venue}</p>}
            {event.image && <img src={`${event.image}`} alt={event.title} className="event-image" />}
            
            <div className="event-actions">
              <button className="edit-btn" onClick={() => editEvent(event.uuid)}>
                <i className="bi bi-pencil-fill"></i> Edit
              </button>
              <button className="delete-btn" onClick={() => deleteEvent(event.uuid)}>
                <i className="bi bi-trash-fill"></i> Delete
              </button>
            </div>
          </div>
        )) : <p>No upcoming events available.</p>}
      </div>
    </div>
  );
}
